import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { deleteTopic, getUserTopics } from "../api/topics";
import { UserContext } from '../functions/Auth';

// component
import ConfirmDialog from '../components/ConfirmDialog';
import TabPanel from '../components/TabPanel';
import TopicList from '../components/TopicList';
import TopicModal from '../components/TopicModal';

// mui
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Typography from '@mui/material/Typography';

// types
type TopicListType = {
    body: string,
    commentCount: number,
    openRange: number, // 公開範囲(招待:0 / 公開:1)
    postStatus: number, // 投稿ステータス(下書き:0 / 投稿:1)
    postTime: string, // 投稿時間 (yyyy-MM-dd HH:mm:ss)
    title: string,
    topicId: number,
    topicMembers: null | {
        email: string,
        userId: number,
        userName: string,
    }[],
    updatedTime: string, // 更新時間 (yyyy-MM-dd HH:mm:ss)
    userId: number,
    userName: string,
}
type OpenTopicListType = Omit<TopicListType, "openRange"> & { openRange: 1 };
type ClosedTopicListType = Omit<TopicListType, "openRange"> & { openRange: 0 };
type DraftTopicListType = Omit<TopicListType, "postStatus"> & { postStatus: 0 };

export default function Topics() {
    const params = useParams();
    const roomId = params.roomId as string;
    const userInfo = React.useContext(UserContext);
    const loginUserId = userInfo?.userId as number;

    // トピックの取得
    const [openTopicList, setOpenTopicList] = useState<OpenTopicListType[]>([]); // 投稿（公開）トピックリスト
    const [closedTopicList, setClosedTopicList] = useState<ClosedTopicListType[]>([]); // 投稿（限定）トピックリスト
    const [draftTopicList, setDraftTopicList] = useState<DraftTopicListType[]>([]); // 下書きのトピックリスト

    const fnGetMyTopics = useCallback(async (roomId: string) => {
        if (loginUserId) {
            fnGetOpenTopics(loginUserId.toString(), roomId);
            fnGetClosedTopics(loginUserId.toString(), roomId);
            fnGetDraftTopics(loginUserId.toString(), roomId);
        }
    }, [loginUserId]);

    async function fnGetOpenTopics(userId: string, roomId: string) {
        const response = await getUserTopics(userId, roomId, "release");
        if (response) {
            const tempTopicList = response.reverse();
            setOpenTopicList(tempTopicList as OpenTopicListType[]);
        }
    }

    async function fnGetClosedTopics(userId: string, roomId: string) {
        const response = await getUserTopics(userId, roomId, "invitation");
        if (response) {
            const tempTopicList = response.reverse();
            setClosedTopicList(tempTopicList as ClosedTopicListType[]);
        }
    }

    async function fnGetDraftTopics(userId: string, roomId: string) {
        const response = await getUserTopics(userId, roomId, "draft");
        if (response) {
            const tempTopicList = response.reverse();
            setDraftTopicList(tempTopicList as DraftTopicListType[]);
        }
    }

    // 公開、限定、下書きトピックリストの作成
    useEffect(() => {
        fnGetMyTopics(roomId);
    }, [roomId, fnGetMyTopics]);

    // タブ
    const [value, setValue] = React.useState(0);
    const handleChangeTab = (event: React.SyntheticEvent, newValue: number) => {
        setValue(newValue);
    };

    function a11yProps(index: number) {
        return {
            id: `tab-${index}`,
            'aria-controls': `tabpanel-${index}`,
        };
    }

    // モーダル
    const [isOpenModal, setIsOpenModal] = useState<boolean>(false);
    const [isEdit, setIsEdit] = useState(false);
    const [topicType, setTopicType] = useState<"draft" | "published" | "new">("new");

    // 更新対象のトピックID
    const [targetEditTopic, setTargetEditTopic] = useState(0);

    // トピックの削除
    const fnDeleteTopic = async (topicId: string) => {
        const result = await deleteTopic(topicId);
        return result;
    }

    // 削除確認ダイアログ
    const [targetDeleteTopic, setTargetDeleteTopic] = useState(0);
    const [isOpenConfirmDialog, setIsOpenConfirmDialog] = useState(false);
    const [isOpenConfirmDialogDraft, setIsOpenConfirmDialogDraft] = useState(false);
    const handleDeleteTopicAgree = () => {
        fnDeleteTopic(targetDeleteTopic.toString())
            .then((result) => {
                fnGetMyTopics(roomId);
            });
    }
    const handleDeleteTopicDisagree = () => {
        setTargetDeleteTopic(0);
    }

    return (
        <Box>
            {/* タブ */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={value} onChange={handleChangeTab} aria-label="tabs" centered={true}>
                    <Tab label="公開" {...a11yProps(0)} />
                    <Tab label="限定" {...a11yProps(1)} />
                    <Tab label="下書き" {...a11yProps(2)} />
                </Tabs>
            </Box>

            {/* ページ情報 */}
            <Box
                sx={{
                    maxWidth: 980,
                    mx: "auto",
                    pt: 3,
                    px: 3,
                }}
            >
                <Typography
                    variant="h5"
                    component="h1"
                    fontWeight="700"
                    gutterBottom
                >トピック管理</Typography>
                <Typography paragraph>あなたが作成したトピックの編集・削除ができます。</Typography>
            </Box>

            {/* タブ - コンテンツ */}
            <TabPanel value={value} index={0}>
                {/* 公開トピック */}
                <TopicList
                    isShowCardActions={true}
                    topicList={openTopicList}
                    setIsEdit={setIsEdit}
                    setIsOpenConfirmDialog={setIsOpenConfirmDialog}
                    setIsOpenModal={setIsOpenModal}
                    setTargetEditTopic={setTargetEditTopic}
                    setTargetDeleteTopic={setTargetDeleteTopic}
                    setTopicType={setTopicType}
                />
            </TabPanel>
            <TabPanel value={value} index={1}>
                {/* 限定トピック */}
                <TopicList
                    isShowCardActions={true}
                    topicList={closedTopicList}
                    setIsEdit={setIsEdit}
                    setIsOpenConfirmDialog={setIsOpenConfirmDialog}
                    setIsOpenModal={setIsOpenModal}
                    setTargetEditTopic={setTargetEditTopic}
                    setTargetDeleteTopic={setTargetDeleteTopic}
                    setTopicType={setTopicType}
                />
            </TabPanel>
            <TabPanel value={value} index={2}>
                {/* 下書きトピック */}
                <TopicList
                    isShowCardActions={true}
                    topicList={draftTopicList}
                    setIsEdit={setIsEdit}
                    setIsOpenConfirmDialog={setIsOpenConfirmDialogDraft}
                    setIsOpenModal={setIsOpenModal}
                    setTargetEditTopic={setTargetEditTopic}
                    setTargetDeleteTopic={setTargetDeleteTopic}
                    setTopicType={setTopicType}
                />
            </TabPanel>

            {/* トピック作成ボタン */}
            <Button
                onClick={() => {
                    setTopicType("new");
                    setIsOpenModal(true);
                }}
                sx={{
                    position: "fixed",
                    right: 16,
                    bottom: 16,
                    zIndex: 100,
                }}
                variant="contained">新規トピック作成
            </Button>

            {/* トピック作成モーダル */}
            <TopicModal
                fnGetRoomTopics={fnGetMyTopics}
                isEdit={isEdit}
                isOpen={isOpenModal}
                setIsEdit={setIsEdit}
                setIsOpen={setIsOpenModal}
                targetEditTopic={targetEditTopic}
                topicType={topicType}
            />

            {/*削除確認ダイアログ（公開、限定）*/}
            <ConfirmDialog
                agreeButtonDisplay={"削除する"}
                body={<>一度削除すると、元に戻すことはできません。<br />削除すると参加メンバーに公開されているトピックは見えなくなります。</>}
                handleClickAgree={handleDeleteTopicAgree}
                handleClickDisagree={handleDeleteTopicDisagree}
                isOpen={isOpenConfirmDialog}
                setIsOpen={setIsOpenConfirmDialog}
                title={"トピックを削除しますか？"}
            />

            {/*削除確認ダイアログ（下書き）*/}
            <ConfirmDialog
                agreeButtonDisplay={"削除する"}
                body={"一度削除すると、元に戻すことはできません。"}
                handleClickAgree={handleDeleteTopicAgree}
                handleClickDisagree={handleDeleteTopicDisagree}
                isOpen={isOpenConfirmDialogDraft}
                setIsOpen={setIsOpenConfirmDialogDraft}
                title={"下書きを削除しますか？"}
            />
        </Box>
    );
}